import type { CartSend, SaleCommand } from './types'

/**
 * Слова связи окна продажи AQSI (RP-SALE-FAST): что кассир видит, пока корзина, оплата или поиск ждут сервер. Строки о связи живут
 * только в файлах `healthTexts.ts` / `health/` (страж `krp/connection-text`): экран и селекторы берут их отсюда, а не пишут сами.
 */

/** Виды отказа попытки, при которых связи с сервером нет (ответа не было): по ним кассиру говорят «Нет связи». */
const NO_ANSWER_KINDS: ReadonlySet<string> = new Set(['network', 'timeout', 'unknown-outcome'])

/** Попытка не получила ответа службы (а не ответ-отказ вроде 503 с кодом причины). */
export function failureWithoutAnswer(failure: CartSend['lastFailure']): boolean {
  return failure === null || NO_ANSWER_KINDS.has(failure.kind)
}

/** Первый шаг после нажатия оплаты: проверка связи с кассой (слова окна оплаты, этап «подготовка»). */
export const PAY_CHECKING_LINK_TEXT = 'Проверяем связь с кассой…'

/** Нажали оплату, а корзина ещё не сохранена: оплата ждёт её сохранения (этап «сохраняем корзину»). */
export const PAY_SAVING_CART_TEXT = 'Сохраняем корзину перед оплатой…'

export const CART_SAVED_TEXT = 'Корзина сохранена'
export const CART_SAVING_TEXT = 'Сохраняем корзину…'

/** Попытка отправки идёт, не первая: «Сохраняем корзину, попытка 3». */
export const cartSavingAttemptText = (attempt: number) => `Сохраняем корзину, попытка ${attempt}`

/**
 * Отправка не удалась по неизвестному исходу и ждёт повтора: правка НЕ сохранена, запрос уйдёт ещё раз сам. `attempt` — номер
 * предстоящей попытки. Ответ службы с причиной («не выполнено») называется её словами.
 */
export function cartRetryText(send: CartSend): string {
  const next = send.attempt + 1
  if (failureWithoutAnswer(send.lastFailure)) return `Нет связи, повторяем — правка не сохранена (попытка ${next})`
  return `Сервер не принял правку, повторяем — она не сохранена (попытка ${next}): ${send.lastFailure?.message ?? ''}`
}

/** Что повторяется: слово для кассира. */
const COMMAND_NOUN: Readonly<Record<SaleCommand['kind'], string>> = {
  pay: 'оплата',
  fiscalize: 'чек',
  cancel: 'отмена',
  outside: 'привязка чека',
}

/**
 * Команда оплаты или чека ждёт паузы перед повтором того же запроса (RP-SALE-FAST, шаг 6). Ключ тот же, поэтому второго списания и
 * второго чека не будет, даже если первый запрос дошёл. `attempt` в тексте — номер предстоящей попытки.
 */
export function commandRetryText(command: SaleCommand): string {
  const noun = COMMAND_NOUN[command.kind]
  const next = command.attempt + 1
  if (failureWithoutAnswer(command.lastFailure)) {
    const guarantee = command.kind === 'fiscalize' ? 'Второго чека не будет.' : 'Повторного списания не будет.'
    return `Нет связи, ждём сеть: ${noun} уйдёт ещё раз тем же запросом (попытка ${next}). ${guarantee}`
  }
  return `Служба не выполнила команду: ${command.lastFailure?.message ?? ''} Повторяем тем же запросом (попытка ${next}).`
}

/** Повторная попытка команды идёт сейчас. */
export function commandAttemptText(command: SaleCommand): string {
  return `Повторяем: ${COMMAND_NOUN[command.kind]} отправляется тем же запросом (попытка ${command.attempt}).`
}

/** Выдача строки поиска не получила ответа: связи нет, запрос повторяется сам (RP-SALE-FAST, шаг 7а). Это не «ничего не найдено». */
export const SEARCH_RETRY_TEXT = 'Нет связи, повторяем поиск'

/** Поиск по Enter или коду сканера ждёт в очереди: «Нет связи, повторяем поиск (попытка N)». */
export const lookupRetryText = (attempt: number) => (attempt > 1 ? `${SEARCH_RETRY_TEXT} (попытка ${attempt})` : SEARCH_RETRY_TEXT)
